import { propsFactory } from '@components/utils';

import type { PropType } from 'vue';

export interface ISrcObject {
  src?: string;
  srcset?: string;
  lazySrc?: string;
  aspect: number;
}

export const makeImageProps = propsFactory(
  {
    /**
     * URL-адрес изображения.
     */
    src: {
      type: [String, Object] as PropType<string | ISrcObject>,
      required: true,
      default: ''
    },
    /**
     * Набор альтернативных изображений для использования в зависимости от размера устройства.
     */
    srcset: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Плейсхолдер для показа в ожидании загрузки основного изображения, обычно небольшая миниатюра в base64-кодировке.
     * Применяется фильтр легкого размытия.
     * ВНИМАНИЕ: Этот параметр не имеет эффекта, если не указаны высота и соотношение сторон.
     */
    lazySrc: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Для использования srcset.
     */
    sizes: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Указывает, что изображения должны быть получены с включенным CORS
     */
    crossorigin: {
      type: String as PropType<'' | 'anonymous' | 'use-credentials'>,
      default: undefined
    },
    /**
     * Указывает, какой referrer будет отправлен при получении ресурса
     */
    referrerpolicy: {
      type: String as PropType<
        | 'no-referrer'
        | 'no-referrer-when-downgrade'
        | 'origin'
        | 'origin-when-cross-origin'
        | 'same-origin'
        | 'strict-origin'
        | 'strict-origin-when-cross-origin'
        | 'unsafe-url'
      >,
      default: undefined
    },
    absolute: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Применяет стили object-position к изображению и элементам placeholder.
     */
    position: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Изменяет размер фонового изображения, чтобы оно занимало весь контейнер.
     */
    cover: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Альтернативный текст для программ чтения с экрана. Оставьте пустым для декоративных изображений.
     */
    alt: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Применяет цвет к фону на котором расположено изображение
     */
    color: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Принудительно рендерит контент компонента в DOM, даже если компонент скрыт. Полезно для SEO оптимизации.
     */
    eager: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Градиент, который нужно применить к изображению. Поддерживает только синтаксис линейного градиента, все остальное должно быть сделано с помощью классов.
     */
    gradient: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Управляет поведением изображения при перетаскивании.
     */
    draggable: {
      type: [Boolean, String] as PropType<boolean | 'true' | 'false'>,
      default: undefined
    },
    /**
     * Параметры, передаваемые Intersection observer.
     */
    options: {
      type: Object as PropType<IntersectionObserverInit>,
      // For more information on types, navigate to:
      // https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
      default: () => ({
        root: undefined,
        rootMargin: undefined,
        threshold: undefined
      })
    }
  },
  'image'
);
